import { makeAutoObservable, observable } from 'mobx';
import GlobalNotification from '../../component/common/snackbar';
import { HeadCell } from '../../component/table/table.component';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import { exportExcel, generateText, getTextTemplate } from '../../constants/tool.fn';
import i18n from '../../i18n';
import { DatabaseTunnelProject, RestaurantTunnelData } from '../../models/restaurant.entity';
import apiManage from '../../request';
import _ from 'lodash';
import { ConditionType } from '../../component/form/search-key-input.component';


/**
 * 隧道管理列表 数据状态管理
 */
class TunnelManagementMobx {

    constructor() {
        makeAutoObservable(this);
    }

    // *********************
    // Attributes
    // *********************

    // 列表数据
    tunnelLists: RestaurantTunnelData[] = [];
    // 表格加载状态值
    isTableLoading = false;
    // 表格顶部下拉框数组
    selectFilterLists: { value: string; label: string; }[] = [
        {
            value: 'nickname',
            label: i18n.t('restaurant_management_page_table_text_restaurant_name'),
        }
    ];
    // 表格顶部搜索框搜索值
    searchValue = '';
    // 排序参数
    extraSortParams: {
        sort_key: string,
        sort_value: string,
        sort_control: number,
    } = { sort_key: '', sort_value: '', sort_control: 0 };
    // 排序map
    sortInfoMap: Record<number | 'default', { value: string, sortControl: number }> = {
        0: { value: '-1', sortControl: 1 },
        1: { value: '1', sortControl: 2 },
        2: { value: '', sortControl: 0 },
        default: { value: '', sortControl: 0 }
    };
    // 要排序的对应的表格map
    sortParamsMap: Record<'tunnel' | 'default', { nickname?: string, group_name?: string, database_port?: string }> = {
        // 集团
        tunnel: {
            nickname: 'nickname',
            group_name: 'group_name',
            database_port: 'database_port',
        },
        default: {}
    };
    // 排序
    sortObj: {
        sort_key?: string,
        sort_value?: string
    } = {};
    // 搜索条件
    conditions = observable<ConditionType>([]);

    // 设置搜索条件
    setConditions(val: ConditionType[]) {
        this.conditions.replace(val);
        this.getTunnelLists();
    }
    // *********************
    // Constant Method
    // *********************
    // 获取表头排序状态值
    getSortStatus(key: string) {
        const listSort = this.extraSortParams;
        if (key === listSort.sort_key) {
            return listSort.sort_control
        }
        return 0;
    }

    // *********************
    // Actions
    // *********************

    /**
     * 初始化
     */
    async init() {
        await this.getTunnelLists();
    }

    /**
     * 获取列表数据
     * @param params
     */
    async getTunnelLists() {
        this.setTableLoading(true);
        await this.fetchTunnelLists();
        this.setTableLoading(false);
    }

    /**
     * 根据参数获取数据
     * @param extraParams
     */
    async fetchTunnelLists() {
        try {
            // 搜索obj
            // eslint-disable-next-line prefer-const
            let searchObj = {};
            this.conditions.forEach((item) => {
                searchObj[item.key] = searchObj[item.key] ? _.concat(searchObj[item.key], item.value) : [item.value];
            })
            // 参数
            const params = {
                pagination_per_page: 9999,
                pagination_page: 1,
                ...this.sortObj,
                ...searchObj,
                sort_value: +this.extraSortParams.sort_value || undefined,
            }
            // 请求
            const result = await apiManage.get_restaurant_tunnel_management_condition(params);
            // 成功
            if (!result.error) {
                // 解构数据
                const { dbResults } = result[0];
                this.setTunnelList(dbResults || []);
            } else {
                // 失败
                GlobalNotification.error(`${i18n.t('tunnel_management_page_failed_to_obtain_restaurant_tunnel_data')}`);
            }
        } catch (e) {
            console.error(e);
            // 失败
            GlobalNotification.error(`${i18n.t('tunnel_management_page_failed_to_obtain_restaurant_tunnel_data')}`);
        }
    }

    /**
     * 设置列表数据
     * @param value
     */
    setTunnelList(value: typeof this.tunnelLists) {
        this.tunnelLists = value;
    }

    /**
     * 格式化列表与导出使用的端口文本。
     * @param databasePort 当前端口值
     * @returns 展示文本
     */
    formatDatabasePortText(databasePort: RestaurantTunnelData['database_port'], project?: RestaurantTunnelData['project']): string {
        const projectPrefix = this.normalizeTunnelProject(project);
        const portText = String(databasePort || '');
        return projectPrefix && portText ? `${projectPrefix}:${portText}` : portText;
    }

    /**
     * 标准化单端口隧道项目，用于拼接下载文件名前缀。
     * @param project 接口返回的项目值
     * @returns 合法项目值或空字符串
     */
    normalizeTunnelProject(project?: RestaurantTunnelData['project']): DatabaseTunnelProject | '' {
        return ['ECCO', 'EFR'].includes(String(project)) ? project as DatabaseTunnelProject : '';
    }

    /**
     * 下载隧道文件
     * @param rowData
     * @returns
     */
    async downloadFile(rowData: RestaurantTunnelData) {
        /**
         * 1.判断数据是否存在，以及是否合法
         */
        const { database_host, database_port, city, nickname, string_id, project } = rowData;
        if (!database_host || !BASE_CHECK_TYPE['description'].regular.test(database_host)) {
            GlobalNotification.error(`${i18n.t('restaurant_page_input_text_restaurant_host')} ${i18n.t(BASE_CHECK_TYPE['description'].helperText, { x: 0, y: 255 })}`);
            return;
        } else if (nickname === '') {
            GlobalNotification.error(`${i18n.t('restaurant_info_page_text_restaurant_name')} ${i18n.t('input_rule_text_required')}`);
            return;
        } else if (city === '') {
            GlobalNotification.error(`${i18n.t('restaurant_info_page_text_city')} ${i18n.t('input_rule_text_required')}`);
            return;
        }
        if (!database_port || !BASE_CHECK_TYPE['maxNumberSix'].regular.test(String(database_port))) {
            GlobalNotification.error(`${i18n.t('restaurant_page_input_text_restaurant_port')} ${i18n.t(BASE_CHECK_TYPE['maxNumberSix'].helperText, { x: 0, y: 6 })}`);
            return;
        }
        // 单端口模式下若接口返回 project，则按项目拼接文件名前缀。
        const projectPrefix = this.normalizeTunnelProject(project);
        // EFR 项目的 ini 内容里，餐厅码需要加上 EFR_ 前缀。
        const tunnelStringId = projectPrefix === 'EFR' ? `EFR_${string_id}` : (string_id || '');
        generateText(`${projectPrefix ? `${projectPrefix}_` : ''}CLOUD_${string_id}_frpc.ini`, getTextTemplate({
            host: database_host,
            port: String(database_port || ''),
            city: city || '',
            nickname: nickname || '',
            string_id: tunnelStringId
        }))
    }

    /**
     * 下载隧道管理表格
     */
    async downloadTunnelExcel() {
        try {
            // 参数
            const params = {
                pagination_per_page: 9999,
                pagination_page: 1,
            }
            // 要下载的数据
            let downloadData: RestaurantTunnelData[] = [];
            // 重新请求接口  获取全部数据
            const result = await apiManage.get_restaurant_tunnel_management_condition(params);
            // 成功
            if (!result.error) {
                // 解构数据
                const { dbResults } = result[0];
                downloadData = dbResults;
            } else {
                // 失败
                GlobalNotification.error(`${i18n.t('tunnel_management_page_failed_to_obtain_restaurant_tunnel_data')}`);
                return;
            }
            // 列宽
            const wpx = [{ wpx: 110 }, { wpx: 120 }, { wpx: 80 }, { wpx: 100 }, { wpx: 140 }, { wpx: 60 },]
            // 文件名
            const fileName = `${i18n.t('tunnel_management_page_text_restaurant_tunnel_management')}.xlsx`;
            // 表头
            const data = [[
                i18n.t('general_text_belonged_group'),
                i18n.t('restaurant_management_page_table_text_restaurant_name'),
                i18n.t('restaurant_page_input_text_restaurant_identification_code'),
                i18n.t('restaurant_management_page_table_text_city'),
                i18n.t('db address'),
                i18n.t('port'),
            ]];
            const newData = data.concat(downloadData.map(item => [
                item?.belonged_group_info?.nickname || '',
                item?.nickname || '',
                item?.string_id || '',
                item?.city || '',
                item?.database_host || '',
                this.formatDatabasePortText(item?.database_port, item?.project),
            ]))
            exportExcel(newData, fileName, wpx);
        } catch (error) {
            console.log(error);
            GlobalNotification.error(i18n.t('general_text_operate_failure'));
        }
    }



    /**
     * 表格排序
     * @param headCell
     */
    async handleSort(headCell: HeadCell) {
        // 排序值 value 1 正序  -1 倒序
        let value = '1';
        let sortControl = 0;
        // 排序对象
        const sortInfo = this.sortInfoMap[headCell.sortStatus || 0] || this.sortInfoMap['default'];
        // 排序值
        value = sortInfo?.value || '';
        // 排序键值控制
        sortControl = sortInfo?.sortControl || 0;
        // 设置排序表头状态
        this.setSortParams({
            sort_key: sortControl === 0 ? '' : headCell.id,
            sort_value: value,
            sort_control: sortControl
        });
        // 排序传参obj
        let sortObj = {} as { sort_key: string; sort_value: string };
        const sortParams = this.sortParamsMap['tunnel'];
        if (sortControl && this.extraSortParams?.sort_value) {
            const sortKey = sortParams[this.extraSortParams?.sort_key as sortKeyType] || '';
            sortObj = {
                sort_key: sortKey,
                sort_value: this.extraSortParams.sort_value,
            };
        }
        this.sortObj = sortObj;
        // 请求数据
        await this.getTunnelLists();
    }

    /**
     * 设置排序参数
     * @param val 排序参数
     */
    setSortParams(val: typeof this.extraSortParams) {
        this.extraSortParams = val;
    }

    /**
     * 设置表格加载状态
     * @param loading
     */
    setTableLoading(loading: boolean) {
        this.isTableLoading = loading;
    }


    /**
     * 设置搜索值
     * @param value
     */
    setSearchValue(value: string) {
        this.searchValue = value;
    }


}

export default TunnelManagementMobx;

// TYPE
type sortKeyType = 'nickname' | 'group_name';